import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, ShoppingCart } from 'lucide-react';
import QuantityStepper from './QuantityStepper';
import ProductOfferNote from './ProductOfferNote';
import ProductImage from './ProductImage';
import { focusRing } from '../styles/ui';
import { productPriceView } from '../utils/offerPresentation';

// The catalogue product card shared by the category listing and the product
// page's Related Products, so both read as one design. Presentational: every
// figure comes from the product projection; cart and favourite actions are
// the caller's existing handlers. Renders an <li> for a list/grid.

const CatalogProductTile = ({ product, view, quantity, onAddToCart, onSetQuantity, onToggleFavourite, favourited }) => {
  const { _id, sku, displayName, thumbnail, sellingUom, isInStock, shortDescription } = product;
  const href = `/products/${sku || _id}`;
  const list = view === 'list';
  // Price, struck price, % off and offer exactly as the backend projected
  // them; the % is only shown when it is a real, positive number.
  const { price, compareAtPrice, discountPercentage: discount } = productPriceView(product);

  // Out of stock: only the purchase action is muted. The card, its photo,
  // name, price, favourite and details link stay fully readable and usable.
  const action = isInStock ? (
    quantity > 0 ? (
      <QuantityStepper quantity={quantity} onChange={(qty) => onSetQuantity(_id, qty)} />
    ) : (
      <button
        type="button"
        onClick={onAddToCart}
        className={`inline-flex items-center justify-center gap-1.5 h-[38px] px-3 sm:px-4 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[13px] font-bold shadow-[0_6px_14px_-7px_rgba(46,139,71,0.7)] whitespace-nowrap transition-[filter,transform] hover:brightness-105 active:scale-[0.98] ${focusRing}`}
      >
        <ShoppingCart size={15} aria-hidden="true" />
        Add to Cart
      </button>
    )
  ) : (
    <button
      type="button"
      disabled
      className="inline-flex items-center justify-center gap-1.5 h-[38px] px-4 rounded-full border border-[#E3E8E3] bg-[#F5F7F5] text-[#8A948D] text-[13px] font-semibold whitespace-nowrap cursor-not-allowed"
    >
      <ShoppingBag size={14} aria-hidden="true" />
      Out of Stock
    </button>
  );

  const unit = sellingUom ? (
    <span className="inline-flex items-center h-[24px] px-2.5 rounded-md text-[11.5px] font-semibold text-emerald-800 bg-[#E9F5E6]">
      {sellingUom}
    </span>
  ) : null;

  const prices = (
    <div className="flex items-baseline flex-wrap gap-x-2">
      <span className="text-[19px] font-extrabold text-emerald-950 leading-none">₹{price ?? '—'}</span>
      {compareAtPrice !== null && <span className="text-[13px] text-gray-400 line-through">₹{compareAtPrice}</span>}
    </div>
  );

  return (
    <li
      className={`group bg-white rounded-[18px] border border-[#E4EDE4] shadow-[0_8px_22px_-16px_rgba(15,51,24,0.32)] transition-[box-shadow,transform] duration-200 hover:-translate-y-[2px] hover:shadow-[0_16px_32px_-18px_rgba(15,51,24,0.4)] overflow-hidden ${
        list ? 'flex flex-col sm:flex-row' : 'flex flex-col'
      }`}
    >
      <div className={`relative shrink-0 ${list ? 'sm:w-[220px]' : ''}`}>
        <Link to={href} className={`block ${focusRing}`} aria-label={displayName}>
          <span className={`block bg-[#F4F7F3] overflow-hidden ${list ? 'aspect-[4/3] sm:aspect-auto sm:h-full sm:min-h-[150px]' : 'aspect-[4/3]'}`}>
            <ProductImage src={thumbnail} alt={displayName} imgClassName="transition-transform duration-500 group-hover:scale-[1.04]" />
          </span>
        </Link>

        <span className="absolute top-3 left-3 flex flex-col items-start gap-1.5 pointer-events-none">
          {discount && (
            <span className="bg-[#F4642C] text-white text-[11.5px] font-bold px-2.5 py-[3px] rounded-full shadow-[0_3px_8px_-2px_rgba(244,100,44,0.55)]">
              {discount}% OFF
            </span>
          )}
          {!isInStock && (
            <span className="bg-white/95 text-[#B42318] border border-[#F4D4D0] text-[11.5px] font-bold px-2.5 py-[3px] rounded-full">
              Out of stock
            </span>
          )}
        </span>

        <button
          type="button"
          onClick={onToggleFavourite}
          aria-label={favourited ? `Remove ${displayName} from favourites` : `Add ${displayName} to favourites`}
          aria-pressed={favourited}
          className={`absolute z-10 top-3 right-3 w-8 h-8 rounded-full bg-white/95 shadow-soft flex items-center justify-center transition-transform hover:scale-105 ${focusRing}`}
        >
          <Heart size={15} className={favourited ? 'text-accent-500 fill-accent-500' : 'text-emerald-950'} aria-hidden="true" />
        </button>
      </div>

      {list ? (
        <div className="flex flex-1 flex-col sm:flex-row sm:items-center gap-4 p-4 sm:px-6 min-w-0">
          <div className="min-w-0 flex-1">
            <Link to={href} className={`${focusRing} block`}>
              <h3 className="text-[16px] font-bold text-emerald-950 leading-snug line-clamp-2 group-hover:text-emerald-800">{displayName}</h3>
            </Link>
            {shortDescription && <p className="text-[13px] text-ink-secondary mt-1 line-clamp-2">{shortDescription}</p>}
            {unit && <div className="mt-2.5">{unit}</div>}
          </div>
          <div className="shrink-0 sm:w-[190px] flex sm:flex-col sm:items-end items-center justify-between gap-2.5">
            <div className="sm:text-right">
              {prices}
              <ProductOfferNote product={product} className="mt-1.5" />
            </div>
            {action}
          </div>
        </div>
      ) : (
        <div className="px-4 pt-3.5 pb-4 flex flex-col flex-1 min-w-0">
          <Link to={href} className={`${focusRing} block`}>
            <h3 className="text-[15px] font-bold text-emerald-950 leading-snug line-clamp-1 group-hover:text-emerald-800">{displayName}</h3>
          </Link>
          <p className="text-[12.5px] text-ink-secondary mt-[3px] line-clamp-1 min-h-[18px]">{shortDescription || ''}</p>
          <div className="mt-3">{prices}</div>
          <ProductOfferNote product={product} className="mt-1.5" />
          <div className="mt-auto pt-3.5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
            {unit ? <span className="self-start sm:self-auto">{unit}</span> : <span className="hidden sm:block" />}
            <span className="flex [&>*]:w-full sm:[&>*]:w-auto">{action}</span>
          </div>
        </div>
      )}
    </li>
  );
};

// Same geometry as a grid card, so nothing jumps when products arrive.
export const CatalogTileSkeleton = () => (
  <li className="bg-white rounded-[18px] border border-[#E4EDE4] overflow-hidden animate-pulse">
    <div className="aspect-[4/3] bg-[#EEF5EC]" />
    <div className="px-4 pt-3.5 pb-4 space-y-2.5">
      <div className="h-4 w-3/4 rounded bg-[#EEF5EC]" />
      <div className="h-3 w-1/2 rounded bg-[#F2F7F1]" />
      <div className="h-5 w-20 rounded bg-[#EEF5EC]" />
      <div className="flex items-center justify-between pt-2">
        <div className="h-6 w-10 rounded bg-[#F2F7F1]" />
        <div className="h-[38px] w-28 rounded-full bg-[#EEF5EC]" />
      </div>
    </div>
  </li>
);

// Cards keep a natural width whatever the count: the tracks auto-fill, so one
// or two products do not stretch, and three form a balanced desktop row.
export const CATALOG_GRID = 'grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-3 sm:gap-[18px]';


export default CatalogProductTile;
